/* eslint-disable style/quote-props */
import type { RatingQuestion } from '@shared/domain/form';
import type { CurrentReview } from '@shared/domain/review';
import { Box, Rating, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import ErrorMessage from '@shared/components/ErrorMessage';
import CustomTooltip from '@shared/components/Tooltip';
import {
  BaseQuestionWrapper,
  ScoreBadge,
  SubtleText,
} from './shared/QuestionStyles';

interface ReviewRatingQuestionProps {
  handleRatingChange: (name: string, value: number | null) => void;
  formFields: any;
  question: RatingQuestion;
  errors: Record<string, string>;
  currentReview?: CurrentReview;
  isReadOnly?: boolean;
  disabled?: boolean;
}

function ReviewRatingQuestion(props: ReviewRatingQuestionProps) {
  const {
    handleRatingChange,
    formFields,
    currentReview,
    question,
    errors,
    isReadOnly,
    disabled,
  } = props;

  const onRatingChange = (
    event: React.SyntheticEvent,
    newValue: number | null,
  ) => {
    handleRatingChange(question.name, newValue);
  };

  const value
    = formFields[question.name]
      || currentReview?.ratings?.find(
        responseRating => responseRating?.type === question.responseName,
      )?.rating
      || 0;

  return (
    <>
      <BaseQuestionWrapper
        data-testid={question.name}
        className="review-rating-question"
        {...({ $autoHeight: true, $flexWrap: 'wrap' } as any)}
      >
        <ScoreBadge className="score-badge" data-testid={`rating-${question.name}-value`}>
          {Number(value) || 0}
          /5
        </ScoreBadge>
        <Box
          className="header-row"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%',
            position: 'relative',
          }}
        >
          <Box
            className="question-section"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography component="legend" id={`${question.name}-label`}>
              <Box
                component="strong"
                sx={{
                  margin: 0,
                  fontSize: '15px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  lineHeight: 1.3,
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <span>{question.question}</span>
                  {question.tooltipText && (
                    <CustomTooltip title={question.tooltipText} />
                  )}
                </Box>
              </Box>
            </Typography>
            {!isReadOnly && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                <SubtleText>Rate from 1 (low) to 5 (high)</SubtleText>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
          }}
        >
          <Rating
            data-testid={`rating-${question.name}-input`}
            onChange={onRatingChange}
            name={question.name}
            size="large"
            value={Number(value)}
            readOnly={isReadOnly}
            disabled={disabled}
            aria-labelledby={`${question.name}-label`}
            sx={{
              margin: '8px 0',
              '& .MuiRating-iconFilled': {
                color: '#78bbf1',
              },
              '& .MuiRating-iconHover': {
                color: '#78bbf1',
                transform: 'scale(1.05)',
                transition: 'transform 0.1s ease',
              },
              '& .MuiRating-iconEmpty': {
                color: '#d1d5db',
              },
            }}
          />
        </Box>
        <Box sx={{ flex: '1 1 auto' }} />
        {errors && !value && errors[question.name] && (
          <ErrorMessage
            key={`${formFields[question.name]}-error`}
            error={errors[question.name]}
          />
        )}
      </BaseQuestionWrapper>
    </>
  );
}

export default ReviewRatingQuestion;
